// Firebase imports
import { initializeApp } from "firebase/app";
import {
    getAuth,
    signOut,
    onAuthStateChanged,
    GoogleAuthProvider,
    signInWithPopup,
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword
} from "firebase/auth";

// Firestore imports
import {
    getFirestore,
    doc,
    setDoc,
    getDoc,
    updateDoc,
    arrayUnion,
    arrayRemove,
    collection,
    getDocs
} from "firebase/firestore";

// --- Parcel asset imports
import defaultProfileImg from './Images/default-profile-picture.png';
import faviconAsset from './Images/Favicon.png';
import googleLogo from './Images/Google-logo.png';

// FIREBASE CONFIG | VALUES FROM .env FILE
const firebaseConfig = {
    apiKey: process.env.FIREBASE_API_KEY,
    authDomain: process.env.FIREBASE_AUTH_DOMAIN,
    projectId: process.env.FIREBASE_PROJECT_ID,
    storageBucket: process.env.FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.FIREBASE_APP_ID
};

// safety check to ensure variables are actually loading from .env
if (!firebaseConfig.apiKey) {
    console.error("CRITICAL ERROR: Firebase API Key is missing. Check your .env file!");
}
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const provider = new GoogleAuthProvider();
const db = getFirestore(app);

// GLOBAL CONSTANTS
    let GAME_DATA = {};           // Holds all games indexed by ID
    let FEATURED_LIST = [];     // Holds games/slides for the top banner
    let ROW_BUCKETS = {};         // Groups games by category (New, Brand, etc.)
    let rotatorInterval = null;  // Reference to the 8-second timer

//UI CONSTANTS
const STATIC_IMAGE_FALLBACK_PATH = defaultProfileImg;
const RESTRICTED_BRANDS = ["Novomatic", "Penguin King"];

// UI ELEMENTS
let bodyEl, viewLoggedOut, viewLoggedIn, emailInputEl, passwordInputEl;
let featuredContainer, featuredBtn, featuredLogo, featuredDesc, featuredRtp, featuredBgLayer;
let detailsModal, detailsName, detailsBg, detailsLogo, detailsBtn, detailsMoreInfoBtn;
let detailsDesc, detailsRtp, detailsBrand, detailsFeatures, closeDetailsBtn;
let categoryModal, closeCategoryBtn, modalTitle, modalGrid;
let searchInput, searchExpandable, suggestionsContainer, searchResultsContainer, searchResultsItems, searchResultsTitle;
let filterSidebar, brandFiltersContainer, featureFiltersContainer, applyFiltersBtn, clearFiltersBtn, closeFilterBtn;
let currentFilters = { brands: [], features: [] };
let calendarBtn, calendarView, calendarContent, homeViewContainer, calendarHomeLink;

// ===== AUTHENTICATION HANDLERS =====
function clearAuthFields() {
    if (emailInputEl) emailInputEl.value = "";
    if (passwordInputEl) passwordInputEl.value = "";
}

function authSignInWithGoogle(e) {
    if (e) e.preventDefault();
    signInWithPopup(auth, provider)
        .then(() => console.log("Google Auth Success"))
        .catch(err => console.error("Google Auth Error:", err.message));
}

function authSignInWithEmail(e) {
    if (e) e.preventDefault();
    const email = emailInputEl?.value;
    const password = passwordInputEl?.value;
    if (!email || !password) return;

    signInWithEmailAndPassword(auth, email, password) // Function from firebase import
        .then(() => clearAuthFields())
        .catch(err => console.error("Sign In Error:", err.message));
}

function authCreateAccountWithEmail(e) {
    if (e) e.preventDefault();
    const email = emailInputEl?.value;
    const password = passwordInputEl?.value;
    if (!email || !password) return;

    createUserWithEmailAndPassword(auth, email, password) // Function from firebase imports
        .then(() => clearAuthFields())
        .catch(err => console.error("Account Creation Error:", err.message));
}

function authSignOut(e) {
    if (e) e.preventDefault();
    if (rotatorInterval) clearInterval(rotatorInterval); // stop 8-second rotation timer so memory isn't wasted
    signOut(auth).catch(err => console.error("Sign Out Error:", err.message));
}


function showLoggedInView() {
    if (viewLoggedOut) viewLoggedOut.style.display = "none";
    if (viewLoggedIn) viewLoggedIn.style.display = "flex";
}

function showLoggedOutView() {
    if (viewLoggedOut) viewLoggedOut.style.display = "flex";
    if (viewLoggedIn) viewLoggedIn.style.display = "none";
}

// ===== UTILITY & HELPERS =====
// When using searchbar, waits for user to stop typing before searching firebase (saves searching after each input letter)
function debounce(func, wait) {
    let timeout;
    return function(...args) {
        const context = this;
        clearTimeout(timeout);
        timeout = setTimeout(() => func.apply(context, args), wait);
    };
}

// Makes rows scrollable (horizontally)
function setupRowInteractions(rowWrapper) {
    if (!rowWrapper) return;
    const leftBtn = rowWrapper.querySelector('.scroll-btn.left');
    const rightBtn = rowWrapper.querySelector('.scroll-btn.right');
    const itemsContainer = rowWrapper.querySelector('.row-items');

    if (!leftBtn || !rightBtn || !itemsContainer) return;

    const checkScroll = () => {
        const tolerance = 5;
        const maxScroll = itemsContainer.scrollWidth - itemsContainer.clientWidth;
        const currentScroll = itemsContainer.scrollLeft;

        if (itemsContainer.scrollWidth <= itemsContainer.clientWidth) {
            leftBtn.classList.add('hidden');
            rightBtn.classList.add('hidden');
            return;
        }
        if (currentScroll <= tolerance) {
            leftBtn.classList.add('hidden');
        } else {
            leftBtn.classList.remove('hidden');
        }
        if (currentScroll >= maxScroll - tolerance) {
            rightBtn.classList.add('hidden');
        } else {
            rightBtn.classList.remove('hidden');
        }
    };

    itemsContainer.addEventListener('scroll', checkScroll);
    window.addEventListener('resize', checkScroll);

    leftBtn.addEventListener('click', (e) => {
        e.preventDefault();
        itemsContainer.scrollBy({ left: -400, behavior: 'smooth' });
    });

    rightBtn.addEventListener('click', (e) => {
        e.preventDefault();
        itemsContainer.scrollBy({ left: 400, behavior: 'smooth' });
    });

    setTimeout(checkScroll, 300);
}

// Retrieves values, switches to all lowercase to find non-case sensitive match
function getValue(data, targetKey) {
    if (!data) return undefined;
    if (data[targetKey] !== undefined) return data[targetKey];
    const cleanTarget = targetKey.toLowerCase().trim();
    for (const key in data) {
        if (key.toLowerCase().trim() === cleanTarget) return data[key];
    }
    return undefined;
}

// ===== Data Normalisation =====
function normalizeGameData(docId, data) {
    const getImg = (key) => {
        const val = getValue(data, key);
        if (val && typeof val === 'string' && val.includes('http')) return val;
        return STATIC_IMAGE_FALLBACK_PATH;
    };

    const getBool = (key) => {
        const val = getValue(data, key);
        return (val === true || String(val).toLowerCase() === 'true');
    };

    // If thumbnail image is missing use logo as backup
    const logoRaw = getImg('Logo Img Src');
    const thumbRaw = getImg('Thumbnail img Src');
    const finalThumb = (thumbRaw !== STATIC_IMAGE_FALLBACK_PATH) ? thumbRaw : logoRaw;

    const featuresArr = [];
    if (getBool('New Game')) featuresArr.push('New Game');
    if (getBool('Coming soon')) featuresArr.push('Coming Soon');
    if (getBool('Free spins')) featuresArr.push('Free Spins');
    if (getBool('Buy Bonus')) featuresArr.push('Buy Bonus');
    if (getBool('Fast')) featuresArr.push('Fast Play');
    if (getBool('Tap Tap')) featuresArr.push('Tap to Play');
    if (getBool('Controversy')) featuresArr.push('Controversy');
    if (getBool('Virtuals')) featuresArr.push('Virtuals');
    if (getBool('Low RTP')) featuresArr.push('Low RTP Warning');

    // RTP math - Converts decimals to percentages
    let rtpValue = getValue(data, 'RTP');
    let rtpString = 'N/A';
    if (rtpValue) {
        const strVal = String(rtpValue);
        if (strVal.includes(':') || strVal.includes(',')) {
            rtpString = strVal;
        } else {
            const match = strVal.match(/(\d+\.?\d*)/);
            const number = match ? parseFloat(match[0]) : null;
            if (number !== null) {
                rtpString = number <= 1 ? (number * 100).toFixed(2) + '%' : number.toFixed(2) + '%';
            }
        }
    }

    return {
        id: docId,
        type: 'game',
        name: getValue(data, 'Name') || "Unknown Title",
        description: getValue(data, 'Description') || "No description provided.",
        brand: getValue(data, 'Brand') || "General",
        url: getValue(data, 'URL') || "#",
        releaseDate: getValue(data, 'Release Date'),
        logoImgSrc: logoRaw,
        backgroundImgSrc: getImg('Bg Img Src'),
        thumbnailImgSrc: finalThumb,
        rtp: rtpString,
        volatility: getValue(data, 'Volatility') || 'N/A',
        maxMultiplier: getValue(data, 'Max Multiplier') || 'N/A',
        reels: getValue(data, 'Reels') || '',
        rows: getValue(data, 'Rows') || '',
        isVisible: getBool('Visible'),
        isFeatured: getBool('Featured'),
        isNew: getBool('New Game'),
        features: featuresArr.join(', ') || 'Standard'
    };
}

function normalizeSlideData(docId, data) {
    const getImg = (key) => {
        const val = getValue(data, key);
        if (val && typeof val === 'string' && val.includes('http')) return val;
        return "";
    };
    let img = getImg('Bg Img Src') || getImg('Logo Img Src') || getImg('imgSrc') || getImg('Image');
    return { id: docId, type: 'slide', imgSrc: img };
}


// CONTENT FETCHING
// fetchAllContent retrieves images from firestore
async function fetchAllContent() {
    FEATURED_LIST = [];
    GAME_DATA = {};
    try {
        // Run both fetches at the exact same time --> speeds up loading
        const [gamesSnap, slidesSnap] = await Promise.all([
            getDocs(collection(db, "games")),
            getDocs(collection(db, "slides")).catch(() => ({ forEach: () => {} })) // Fail gracefully if no slides
        ]);

        gamesSnap.forEach(doc => {
            const game = normalizeGameData(doc.id, doc.data());
            GAME_DATA[doc.id] = game;
            if (game.isVisible && game.isFeatured) FEATURED_LIST.push(game);
        });

        slidesSnap.forEach(doc => {
            const slide = normalizeSlideData(doc.id, doc.data());
            if (slide.imgSrc && slide.imgSrc.length > 20) FEATURED_LIST.push(slide);
        });

        return true;
    } catch (e) {
        console.error("Critical Firestore Error:", e);
        return false;
    }
}

function startFeaturedRotator() {
    if (FEATURED_LIST.length === 0) return;
    let currentIndex = 0;
    const featuredElements = [featuredLogo, featuredDesc, featuredRtp, featuredBtn];

    const updateFeaturedUI = (item) => {
        if (item.type === 'game') {
            if(featuredLogo) { featuredLogo.src = item.logoImgSrc; featuredLogo.classList.remove('hidden'); }
            if(featuredDesc) { featuredDesc.textContent = item.description; featuredDesc.classList.remove('hidden'); }
            if(featuredRtp) { featuredRtp.textContent = item.rtp ? `RTP: ${item.rtp}` : ''; featuredRtp.classList.remove('hidden'); }
            if(featuredBtn) { featuredBtn.setAttribute('data-game-id', item.id); featuredBtn.classList.remove('hidden'); }
            if(featuredContainer) featuredContainer.classList.remove('slide-active');
        } else {
            featuredElements.forEach(el => el?.classList.add('hidden'));
            if(featuredContainer) featuredContainer.classList.add('slide-active');
        }
    };

    const renderFeaturedItem = () => {
        if (currentIndex >= FEATURED_LIST.length) currentIndex = 0;
        const item = FEATURED_LIST[currentIndex];
        const bgUrl = item.type === 'game' ? item.backgroundImgSrc : item.imgSrc;

        if(featuredBgLayer) featuredBgLayer.classList.add('fade-out');
        featuredElements.forEach(el => { if(el) el.style.opacity = '0'; });

        const bgLoadPromise = new Promise((resolve) => {
            const img = new Image();
            img.src = bgUrl;
            img.onload = () => resolve(bgUrl);
            img.onerror = () => resolve(bgUrl);
        });

        Promise.all([new Promise(r => setTimeout(r, 500)), bgLoadPromise])
            .then(() => {
                if(featuredBgLayer) featuredBgLayer.style.backgroundImage = `url("${bgUrl}")`;
                updateFeaturedUI(item);
                requestAnimationFrame(() => {
                    if(featuredBgLayer) featuredBgLayer.classList.remove('fade-out');
                    featuredElements.forEach(el => { if(el) el.style.opacity = '1'; });
                });
            });

        currentIndex = (currentIndex + 1) % FEATURED_LIST.length;
    };

    renderFeaturedItem();
    if (rotatorInterval) clearInterval(rotatorInterval);
    rotatorInterval = setInterval(renderFeaturedItem, 8000);
}

async function loadUiIcons() {
    try {
        const docRef = doc(db, "site-settings", "ui_icons");
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
            const data = docSnap.data();
            console.log("Firestore Icon Data:", data); // Check your console (F12) to see these!

            // 1. Target the internal <img> tags specifically
            const calImg = document.getElementById("calendar-icon-img");
            if (calImg && data.calendar) calImg.src = data.calendar;

            const filterImg = document.getElementById("filter-icon-img");
            if (filterImg && data.filter) filterImg.src = data.filter;

            // 2. Set Search Icon
            const searchImg = document.getElementById("search-icon");
            if (searchImg && data.search) searchImg.src = data.search;

            // 3. Set Google Logo (for the sign-in button)
            const googleImg = document.getElementById("google-logo-img");
            if (googleImg && data.google_logo) googleImg.src = data.google_logo;

            console.log("UI Icons successfully updated from Firestore");
        } else {
            console.warn("No 'ui_icons' document found in Firestore!");
        }
    } catch (e) {
        console.error("Error loading UI icons:", e);
    }
}

// ===== Calendar rendering =====
function renderCalendar() {
    const calendarContainer = document.getElementById('calendar-content');
    if (!calendarContainer) return;
    calendarContainer.innerHTML = '';
    const today = new Date();
    const monthsToShow = 3;

    for (let i = 0; i < monthsToShow; i++) {
        const currentMonthDate = new Date(today.getFullYear(), today.getMonth() + i, 1);
        const year = currentMonthDate.getFullYear();
        const month = currentMonthDate.getMonth();
        const monthName = currentMonthDate.toLocaleString('default', { month: 'long', year: 'numeric' });
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const startDayIndex = currentMonthDate.getDay();

        const monthSection = document.createElement('div');
        monthSection.className = 'calendar-month-section';
        monthSection.innerHTML = `
            <h2 class="calendar-month-title">${monthName}</h2>
            <div class="calendar-grid-header">
                <div class="weekday-header">Sun</div>
                <div class="weekday-header">Mon</div>
                <div class="weekday-header">Tue</div>
                <div class="weekday-header">Wed</div>
                <div class="weekday-header">Thu</div>
                <div class="weekday-header">Fri</div>
                <div class="weekday-header">Sat</div>
            </div>
            <div class="calendar-month-grid"></div>
        `;

        const grid = monthSection.querySelector('.calendar-month-grid');
        for (let j = 0; j < startDayIndex; j++) {
            grid.innerHTML += `<div class="calendar-day-cell empty"></div>`;
        }

        for (let day = 1; day <= daysInMonth; day++) {
            const gamesForDay = Object.values(GAME_DATA).filter(game => {
                if (!game.releaseDate || !game.isVisible) return false;
                const gameDate = new Date(game.releaseDate);
                if (isNaN(gameDate.getTime())) return false;
                return gameDate.getDate() === day &&
                    gameDate.getMonth() === month &&
                    gameDate.getFullYear() === year;
            });

            const cell = document.createElement('div');
            cell.className = 'calendar-day-cell';
            const isToday = (day === today.getDate() && month === today.getMonth() && year === today.getFullYear());
            if (isToday) cell.classList.add('today');

            let cellContent = `<span class="day-number">${day}</span>`;
            if (gamesForDay.length > 0) {
                cellContent += `<div class="calendar-split-container">`;
                gamesForDay.forEach(game => {
                    cellContent += `
                        <div class="calendar-game-split" 
                             style="background-image: url('${game.thumbnailImgSrc}');"
                             title="${game.name}"
                             data-game-id="${game.id}">
                             <div class="day-overlay"></div>
                        </div>`;
                });
                cellContent += `</div>`;
            }
            cell.innerHTML = cellContent;
            cell.querySelectorAll('.calendar-game-split').forEach(split => {
                split.addEventListener('click', handleThumbnailClick);
            });
            grid.appendChild(cell);
        }
        calendarContainer.appendChild(monthSection);
    }
}

// ===== Dynamic row rendering =====
function renderDynamicGameRows() {
    const container = document.getElementById('dynamic-rows-container');
    if (!container) return;
    container.innerHTML = '';
    const filtersActive = currentFilters.brands.length > 0 || currentFilters.features.length > 0;

    if (filtersActive) {
        const filtered = Object.values(GAME_DATA).filter(game => {
            if (!game.isVisible) return false;
            if (currentFilters.brands.length > 0 && !currentFilters.brands.includes(game.brand)) return false;
            if (currentFilters.features.length > 0) {
                for (const req of currentFilters.features) {
                    if (!game.features.includes(req)) return false;
                }
            }
            return true;
        });
        displayFilteredResults(filtered);
    } else {
        if (searchResultsContainer) searchResultsContainer.style.display = 'none';
        if (featuredContainer) featuredContainer.style.display = 'flex';
        if (container) container.style.display = 'block';

        renderContinuePlayingRow();
        ROW_BUCKETS = {};
        const config = [
            { title: "New Games", field: "isNew" },
            { title: "Coming Soon", field: "isComingSoon" },
            { title: "Buy Bonus Games", field: "hasBuyBonus" },
            { title: "Free Spins Feature", field: "hasFreeSpins" },
            { title: "Tap Tap Games", field: "isTapTap" }
        ];

        config.forEach(cfg => ROW_BUCKETS[cfg.title] = []);
        Object.values(GAME_DATA).forEach(game => {
            if (!game.isVisible) return;
            config.forEach(cfg => { if (game[cfg.field]) ROW_BUCKETS[cfg.title].push(game); });
            const brand = game.brand || "Other Games";
            if (!ROW_BUCKETS[brand]) ROW_BUCKETS[brand] = [];
            ROW_BUCKETS[brand].push(game);
        });

        config.forEach(cfg => {
            if (ROW_BUCKETS[cfg.title].length > 0) {
                container.insertAdjacentHTML('beforeend', buildRowHtml(cfg.title, ROW_BUCKETS[cfg.title]));
            }
        });

        const special = config.map(x => x.title);
        Object.keys(ROW_BUCKETS).filter(k => !special.includes(k)).sort().forEach(brand => {
            if (ROW_BUCKETS[brand].length > 0) {
                container.insertAdjacentHTML('beforeend', buildRowHtml(brand, ROW_BUCKETS[brand]));
            }
        });
        attachRowListeners();
    }
}

function buildRowHtml(title, games) {
    const cards = games.slice(0, 12).map(game => `
        <div>
            <a href="#" data-game-id="${game.id}">
                <img src="${game.thumbnailImgSrc}" alt="${game.name}" loading="lazy" 
                     onerror="this.src='${STATIC_IMAGE_FALLBACK_PATH}'">
            </a>
        </div>
    `).join('');

    return `
        <div class="row">
            <div class="row-header">
                <h2>${title}</h2>
                ${games.length > 12 ? `<button class="view-all-btn" data-category="${title}">View All (${games.length})</button>` : ''}
            </div>
            <div class="row-content-wrapper">
                <button class="scroll-btn left">&lsaquo;</button>
                <div class="row-items">
                    ${cards}
                </div>
                <button class="scroll-btn right">&rsaquo;</button>
            </div>
        </div>`;
}

function attachRowListeners() {
    document.querySelectorAll('[data-game-id]').forEach(el => el.addEventListener('click', handleThumbnailClick));
    document.querySelectorAll('.row-content-wrapper').forEach(wrapper => setupRowInteractions(wrapper));
    document.querySelectorAll('.view-all-btn').forEach(btn => {
        btn.addEventListener('click', (e) => openCategoryModal(e.target.getAttribute('data-category')));
    });
}

/* ==========================================================================
   SEARCH & SUGGESTIONS
   ========================================================================== */
function renderSearchSuggestions(query) {
    const qLower = query.toLowerCase();
    suggestionsContainer.innerHTML = '';

    if (qLower.length < 2) {
        suggestionsContainer.style.display = 'none';
        return;
    }

    const filtered = Object.values(GAME_DATA).filter(game => {
        if (!game.isVisible) return false;
        return game.name.toLowerCase().includes(qLower) ||
            game.brand.toLowerCase().includes(qLower) ||
            game.features.toLowerCase().includes(qLower);
    }).sort((a, b) => a.name.localeCompare(b.name)).slice(0, 8);

    if (filtered.length > 0) {
        suggestionsContainer.innerHTML = filtered.map(game => `
            <div class="suggestion-item" data-game-id="${game.id}">
                <img src="${game.thumbnailImgSrc}" alt="${game.name}">
                <div class="suggestion-text">
                    <strong>${game.name}</strong>
                    <span>${game.brand} | ${RESTRICTED_BRANDS.includes(game.brand) ? 'More Info' : 'Play'}</span>
                </div>
            </div>
        `).join('');

        suggestionsContainer.style.display = 'flex';
        suggestionsContainer.querySelectorAll('.suggestion-item').forEach(item => {
            item.addEventListener('click', () => {
                const id = item.getAttribute('data-game-id');
                searchInput.value = GAME_DATA[id].name;
                suggestionsContainer.style.display = 'none';
                openGameDetailsModal(id);
            });
        });
    } else {
        suggestionsContainer.style.display = 'none';
    }
}

function renderSearchResults(query) {
    const qLower = query.toLowerCase();
    suggestionsContainer.style.display = 'none';

    if (qLower.length < 2) {
        if(searchResultsContainer) searchResultsContainer.style.display = 'none';
        document.getElementById('dynamic-rows-container').style.display = 'block';
        if (featuredContainer) featuredContainer.style.display = 'flex';
        renderContinuePlayingRow();
        return;
    }

    const filtered = Object.values(GAME_DATA).filter(game => {
        if (!game.isVisible) return false;
        return game.name.toLowerCase().includes(qLower) ||
            game.brand.toLowerCase().includes(qLower) ||
            game.features.toLowerCase().includes(qLower);
    });

    if (searchResultsItems) {
        searchResultsItems.innerHTML = '';
        if (filtered.length > 0) {
            searchResultsTitle.textContent = `Search Results (${filtered.length}) for "${query}"`;
            searchResultsItems.innerHTML = filtered.map(game => `
                <div><a href="#" data-game-id="${game.id}"><img src="${game.thumbnailImgSrc}" alt="${game.name}"></a></div>
            `).join('');
        } else {
            searchResultsTitle.textContent = `No results found for "${query}"`;
        }

        searchResultsContainer.style.display = 'block';
        if (featuredContainer) featuredContainer.style.display = 'none';
        document.getElementById('dynamic-rows-container').style.display = 'none';
        document.getElementById('continue-playing-row').style.display = 'none';

        searchResultsItems.querySelectorAll('a[data-game-id]').forEach(link => {
            link.addEventListener('click', handleThumbnailClick);
        });
    }
}

function displayFilteredResults(filteredGames) {
    if (!searchResultsContainer) return;

    document.getElementById('dynamic-rows-container').style.display = 'none';
    if (featuredContainer) featuredContainer.style.display = 'none';
    document.getElementById('continue-playing-row').style.display = 'none';

    let titleParts = [];
    if (currentFilters.brands.length > 0) titleParts.push(`Brands: ${currentFilters.brands.join(', ')}`);
    if (currentFilters.features.length > 0) titleParts.push(`Features: ${currentFilters.features.join(', ')}`);

    let finalTitle = `Filtered Results (${filteredGames.length})`;
    if (titleParts.length > 0) finalTitle += ` - ${titleParts.join(' | ')}`;

    searchResultsItems.innerHTML = '';
    if (filteredGames.length > 0) {
        searchResultsTitle.textContent = finalTitle;
        searchResultsItems.innerHTML = filteredGames.map(game => `
            <div><a href="#" data-game-id="${game.id}"><img src="${game.thumbnailImgSrc}" alt="${game.name}"></a></div>
        `).join('');
        searchResultsItems.querySelectorAll('a[data-game-id]').forEach(link => link.addEventListener('click', handleThumbnailClick));
    } else {
        searchResultsTitle.textContent = `${finalTitle} (No results found)`;
    }

    searchResultsContainer.style.display = 'block';
}

/* ==========================================================================
   MODAL LOGIC
   ========================================================================== */
function handleThumbnailClick(e) {
    e.preventDefault();
    const gameId = e.currentTarget.getAttribute('data-game-id');
    if (gameId) openGameDetailsModal(gameId);
}

function openGameDetailsModal(gameId) {
    const game = GAME_DATA[gameId];
    if (!game) return;

    if(detailsName) detailsName.textContent = game.name;
    if(detailsBg) detailsBg.style.backgroundImage = `url("${game.backgroundImgSrc}")`;
    if(detailsLogo) detailsLogo.src = game.logoImgSrc;

    const restricted = RESTRICTED_BRANDS.includes(game.brand);

    if (detailsBtn) {
        detailsBtn.setAttribute('data-game-id', game.id);
        detailsBtn.style.display = restricted ? 'none' : 'inline-flex';
    }

    if (detailsMoreInfoBtn) {
        detailsMoreInfoBtn.setAttribute('data-game-id', game.id);
        detailsMoreInfoBtn.href = `more-info.html?id=${gameId}`;
    }

    if(detailsDesc) detailsDesc.textContent = game.description;
    if(detailsRtp) detailsRtp.textContent = game.rtp;
    if(detailsBrand) detailsBrand.textContent = game.brand;
    if(detailsFeatures) detailsFeatures.textContent = game.features;

    detailsModal.classList.add('active');
}

function openCategoryModal(categoryName) {
    const games = ROW_BUCKETS[categoryName];
    if (!games) return;

    if(modalTitle) modalTitle.textContent = categoryName;
    if(modalGrid) {
        modalGrid.innerHTML = games.map(game => `
            <div class="modal-grid-item">
                <a href="#" data-game-id="${game.id}">
                    <img src="${game.thumbnailImgSrc}" alt="${game.name}">
                </a>
            </div>
        `).join('');

        modalGrid.querySelectorAll('a').forEach(link => {
            link.addEventListener('click', (e) => {
                e.preventDefault();
                categoryModal.classList.remove('active');
                handleThumbnailClick(e);
            });
        });
    }
    categoryModal.classList.add('active');
}

// ===== HISTORY & GAME LAUNCH =====
function launchGame(gameId) {
    const game = GAME_DATA[gameId];
    if (game) {
        const encodedUrl = encodeURIComponent(game.url);
        window.open(`game-view.html?url=${encodedUrl}&name=${encodeURIComponent(game.name)}`, '_blank');
        trackGameClick(gameId);
    }
}

async function trackGameClick(gameId) {
    const user = auth.currentUser;
    if (!user) return;
    const ref = doc(db, 'users', user.uid);
    try {
        // If the game is already in continue playing this makes sure the most recent played is at the front
        await updateDoc(ref, { gameHistory: arrayRemove(gameId) });
        await updateDoc(ref, { gameHistory: arrayUnion(gameId) });
    } catch (e) {
        try { await setDoc(ref, { gameHistory: [gameId] }); } catch(err){}
    }
    await renderContinuePlayingRow();
}

async function renderContinuePlayingRow() {
    const container = document.getElementById('continue-playing-items');
    const rowParent = document.getElementById("continue-playing-row");
    if (!container || !rowParent) return;

    if (searchResultsContainer?.style.display === 'block') {
        rowParent.style.display = 'none';
        return;
    }

    container.innerHTML = '';
    let history = [];
    const user = auth.currentUser;

    if (user) {
        try {
            const snap = await getDoc(doc(db, 'users', user.uid));
            if (snap.exists() && snap.data().gameHistory) {
                history = snap.data().gameHistory.reverse().slice(0, 12);
            }
        } catch (e) { console.error("History fetch error:", e); }
    }

    const valid = history.filter(id => GAME_DATA[id] && GAME_DATA[id].isVisible);

    if (valid.length > 0) {
        container.innerHTML = valid.map(id => `
            <div>
                <a href="#" data-game-id="${id}">
                    <img src="${GAME_DATA[id].thumbnailImgSrc}" alt="history">
                </a>
            </div>
        `).join('');

        document.querySelectorAll('#continue-playing-items a').forEach(link => {
            link.addEventListener('click', handleThumbnailClick);
        });

        rowParent.style.display = 'block';
        setupRowInteractions(rowParent.querySelector('.row-content-wrapper'));
    } else {
        rowParent.style.display = 'none';
    }
}

function populateFilterSidebar() {
    if (!brandFiltersContainer || !featureFiltersContainer) return;

    brandFiltersContainer.querySelectorAll('.filter-options').forEach(el => el.remove());
    featureFiltersContainer.querySelectorAll('.filter-options').forEach(el => el.remove());

    const brandsSet = new Set();
    const featuresSet = new Set();

    Object.values(GAME_DATA).forEach(game => {
        brandsSet.add(game.brand);
        if (game.features && game.features !== 'Standard') {
            game.features.split(',').forEach(f => featuresSet.add(f.trim()));
        }
    });

    const bHtml = Array.from(brandsSet).sort().map(b => `
        <div class="filter-option">
            <input type="checkbox" name="brand" value="${b}">
            <label>${b}</label>
        </div>
    `).join('');

    const fHtml = Array.from(featuresSet).sort().map(f => `
        <div class="filter-option">
            <input type="checkbox" name="feature" value="${f}">
            <label>${f}</label>
        </div>
    `).join('');

    brandFiltersContainer.querySelector('h3').insertAdjacentHTML('afterend', `<div class="filter-options">${bHtml}</div>`);
    featureFiltersContainer.querySelector('h3').insertAdjacentHTML('afterend', `<div class="filter-options">${fHtml}</div>`);

    if (filterSidebar) {
        filterSidebar.querySelectorAll('input[type="checkbox"]').forEach(cb => {
            cb.addEventListener('change', () => {
                const selectedBrands = [];
                const selectedFeatures = [];
                filterSidebar.querySelectorAll('input[name="brand"]:checked').forEach(c => selectedBrands.push(c.value));
                filterSidebar.querySelectorAll('input[name="feature"]:checked').forEach(c => selectedFeatures.push(c.value));
                currentFilters = { brands: selectedBrands, features: selectedFeatures };
            });
        });
    }
}

/* ==========================================================================
   INITIALIZATION
   ========================================================================== */
document.addEventListener('DOMContentLoaded', () => {
    // 1. Assign static pointers
    bodyEl = document.body;
    viewLoggedOut = document.getElementById("logged-out-view");
    viewLoggedIn = document.getElementById("logged-in-view");
    emailInputEl = document.getElementById("email-input");
    passwordInputEl = document.getElementById("password-input");

    const userProfilePictureEl = document.getElementById("user-profile-picture");
    const popupProfilePictureEl = document.getElementById("popup-profile-picture");
    const userEmailDisplayEl = document.getElementById("user-email-display");
    calendarView = document.getElementById("calendar-view");
    homeViewContainer = document.getElementById("home-view-container");
    calendarHomeLink = document.getElementById("calendar-home-link");

    featuredContainer = document.getElementById("featured-game");
    featuredBtn = document.getElementById("featured-play-btn");
    featuredLogo = document.getElementById("featured-logo");
    featuredDesc = document.getElementById("featured-description");
    featuredRtp = document.getElementById("featured-rtp");
    featuredBgLayer = document.getElementById("featured-background");

    categoryModal = document.getElementById("category-modal");
    closeCategoryBtn = document.getElementById("close-category-modal");
    modalTitle = document.getElementById("modal-category-title");
    modalGrid = document.getElementById("modal-game-grid");

    detailsModal = document.getElementById("game-details-modal");
    closeDetailsBtn = document.getElementById("close-details-modal");
    detailsName = document.getElementById("details-name");
    detailsBg = document.getElementById("details-background");
    detailsLogo = document.getElementById("details-logo");
    detailsBtn = document.getElementById("details-play-btn");
    detailsDesc = document.getElementById("details-description");
    detailsRtp = document.getElementById("details-rtp");
    detailsBrand = document.getElementById("details-brand");
    detailsFeatures = document.getElementById("details-features");

    // THE CRITICAL BUTTON
    detailsMoreInfoBtn = document.getElementById("details-more-info-btn");

    searchInput = document.getElementById("search-input");
    searchExpandable = document.getElementById("search-menu-item");
    searchResultsContainer = document.getElementById("search-results-container");
    searchResultsItems = document.getElementById("search-results-items");
    searchResultsTitle = document.getElementById("search-results-title");

    const favicon = document.querySelector('link[rel="icon"]');
    if (favicon) favicon.href = faviconAsset;

    const googleLogoImg = document.getElementById("google-logo-img");
    if (googleLogoImg) googleLogoImg.src = googleLogo;

    const accountArea = document.querySelector('.account-area');
    suggestionsContainer = document.createElement('div');
    suggestionsContainer.id = 'search-suggestions';
    suggestionsContainer.className = 'search-suggestions';
    if (accountArea?.parentNode) accountArea.parentNode.insertBefore(suggestionsContainer, accountArea.nextSibling);

    filterSidebar = document.getElementById("filter-sidebar");
    brandFiltersContainer = document.getElementById("brand-filters");
    featureFiltersContainer = document.getElementById("feature-filters");
    applyFiltersBtn = document.getElementById("apply-filters-btn");
    clearFiltersBtn = document.getElementById("clear-filters-btn");

    // 2. Attach Listeners
    document.getElementById("sign-in-with-google-btn")?.addEventListener("click", authSignInWithGoogle);
    document.getElementById("sign-in-btn")?.addEventListener("click", authSignInWithEmail);
    document.getElementById("create-account-btn")?.addEventListener("click", authCreateAccountWithEmail);
    document.getElementById("logout-btn")?.querySelector("button")?.addEventListener("click", authSignOut);

    const popup = document.getElementById("popup");
    document.getElementById("account-menu-item")?.addEventListener('click', () => popup.classList.add("open-popup"));
    document.getElementById("close-popup-btn")?.addEventListener('click', () => popup.classList.remove("open-popup"));

    document.getElementById("filter-btn")?.addEventListener('click', () => filterSidebar.classList.add('active'));
    document.getElementById("close-filter-btn")?.addEventListener('click', () => filterSidebar.classList.remove('active'));

    document.getElementById("calendar-btn")?.addEventListener('click', () => {
        if (homeViewContainer) homeViewContainer.style.display = "none";
        if (calendarView) calendarView.style.display = "block";
        if (searchResultsContainer) searchResultsContainer.style.display = "none";
    });

    if (calendarHomeLink) {
        calendarHomeLink.addEventListener('click', (e) => {
            e.preventDefault();
            if (calendarView) calendarView.style.display = "none";
            if (homeViewContainer) homeViewContainer.style.display = "block";
            if (featuredContainer) featuredContainer.style.display = "flex";
        });
    }

    if (featuredBtn) {
        featuredBtn.addEventListener('click', (e) => {
            e.preventDefault();
            const id = e.currentTarget.getAttribute('data-game-id');
            if (id) launchGame(id);
        });
    }

    closeCategoryBtn?.addEventListener('click', () => categoryModal.classList.remove('active'));
    closeDetailsBtn?.addEventListener('click', () => detailsModal.classList.remove('active'));

    if (detailsBtn) {
        detailsBtn.addEventListener('click', (e) => {
            const id = e.currentTarget.getAttribute('data-game-id');
            if (id) {
                detailsModal.classList.remove('active');
                launchGame(id);
            }
        });
    }

    /* === FIX START: ADD EXPLICIT LISTENER FOR MORE INFO === */
    if (detailsMoreInfoBtn) {
        detailsMoreInfoBtn.addEventListener('click', (e) => {
            e.preventDefault(); // Stop default anchor behavior in case href is '#'
            const id = e.currentTarget.getAttribute('data-game-id');
            console.log("More Info Clicked for ID:", id); // DEBUG LOG

            if (id) {
                // Force navigation manually
                window.location.href = `more-info.html?id=${id}`;
            } else {
                console.error("No Data-ID found on More Info button");
            }
        });
    }
    /* === FIX END === */

    applyFiltersBtn?.addEventListener('click', () => {
        filterSidebar.classList.remove('active');
        renderDynamicGameRows();
    });

    clearFiltersBtn?.addEventListener('click', () => {
        filterSidebar.querySelectorAll('input[type="checkbox"]').forEach(c => c.checked = false);
        currentFilters = { brands: [], features: [] };
        filterSidebar.classList.remove('active');
        renderDynamicGameRows();
    });

    if (searchInput && searchExpandable) {
        searchInput.addEventListener('focus', () => {
            if (window.innerWidth <= 768) viewLoggedIn.classList.add('search-full-width-mobile');
        });

        searchInput.addEventListener('blur', () => {
            setTimeout(() => {
                const focus = document.activeElement;
                if (suggestionsContainer?.contains(focus)) return;
                suggestionsContainer.style.display = 'none';
                if (window.innerWidth <= 768) viewLoggedIn.classList.remove('search-full-width-mobile');
                if (searchInput.value.trim() === '') {
                    renderSearchResults('');
                }
            }, 250);
        });

        searchInput.addEventListener('input', debounce(() => {
            renderSearchSuggestions(searchInput.value.trim());
        }, 300));
    }

    // 3. Auth Listener
    onAuthStateChanged(auth, (user) => {
        if (bodyEl.classList.contains('loading-state')) {
            bodyEl.classList.remove('loading-state');
        }
        const photo = (user && user.photoURL) ? user.photoURL : STATIC_IMAGE_FALLBACK_PATH;

        if (user) {
            showLoggedInView();
            if (userProfilePictureEl) userProfilePictureEl.src = photo;
            if (popupProfilePictureEl) popupProfilePictureEl.src = photo;
            if (userEmailDisplayEl) userEmailDisplayEl.textContent = user.email;
        } else {
            showLoggedOutView();
            if (userProfilePictureEl) userProfilePictureEl.src = STATIC_IMAGE_FALLBACK_PATH;
        }
        renderContinuePlayingRow().then(() => {
            console.log("User history row refreshed.");
        }).catch(err => console.error("History refresh failed:", err));
    });

    // 4. Data Bootstrapper
    (async () => {
        const success = await fetchAllContent();
        if (success) {
            await loadUiIcons();
            populateFilterSidebar();
            renderDynamicGameRows();
            startFeaturedRotator();
            renderCalendar()
        }
    })();
});